import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";

export interface UnsavedChanges {
	/** True while this section holds changes the user has not saved. */
	when: boolean;
	/** Shown in this section's confirm dialog. */
	message: string;
	/** Called once every section has been confirmed. Reset the unsaved changes here. */
	onConfirm?: () => void;
}

/** A registered section, read through the ref so the provider always sees current props. */
type UnsavedChangesSource = { current: UnsavedChanges };

const UnsavedChangesContext = createContext<Set<UnsavedChangesSource> | undefined>(undefined);

function pendingSections(sources: Set<UnsavedChangesSource>) {
	return Array.from(sources, (source) => source.current).filter((section) => section.when);
}

/**
 * Owns the app's one navigation blocker.
 *
 * react-router supports a single blocker per router — `shouldBlockNavigation` only ever
 * consults the most recently registered one and warns about the rest.
 */
export function UnsavedChangesProvider(props: { children: React.ReactNode }) {
	const sourcesRef = useRef(new Set<UnsavedChangesSource>());

	const blocker = useBlocker(
		useCallback(() => pendingSections(sourcesRef.current).length > 0, []),
	);

	useEffect(() => {
		if (blocker.state !== "blocked") return;

		const pending = pendingSections(sourcesRef.current);

		if (!pending.every((section) => window.confirm(section.message))) {
			blocker.reset();
			return;
		}

		pending.forEach((section) => section.onConfirm?.());
		blocker.proceed();
	}, [blocker]);

	useEffect(() => {
		const handler = (event: BeforeUnloadEvent) => {
			if (pendingSections(sourcesRef.current).length === 0) return;
			event.preventDefault();
			event.returnValue = "";
		};

		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, []);

	return (
		<UnsavedChangesContext.Provider value={sourcesRef.current}>
			{props.children}
		</UnsavedChangesContext.Provider>
	);
}

/**
 * Registers a section's unsaved changes with the nearest `UnsavedChangesProvider`. Render
 * `NavigationPrompt` instead of calling this directly.
 */
export function useUnsavedChanges(section: UnsavedChanges) {
	const sources = useContext(UnsavedChangesContext);

	const sectionRef = useRef(section);
	sectionRef.current = section;

	useEffect(() => {
		if (sources === undefined) {
			console.error(
				"NavigationPrompt rendered outside an UnsavedChangesProvider; navigation will not be blocked.",
			);
			return;
		}

		sources.add(sectionRef);
		return () => {
			sources.delete(sectionRef);
		};
	}, [sources]);
}
